import React from "react";
import {
  requestResponse,
  requestIsSuccess,
  requestIsBusy,
} from "@redriver/cinnamon";
import { RepInfoSteps, PassengerTabs } from "constants/enums";
import { ActionTypes } from "./actions";

const RequiredFields = ({ additionalText }) => (
  <>
    Fields marked with a <span className="red">*</span> must be completed
    {additionalText}
  </>
);

const buildSteps = () => {
  const steps = [
    {
      key: RepInfoSteps.Personal,
      name: "Personal Details",
      title: "Personal Details",
      description: (
        <>
          Please enter all of your personal information accurately, as it
          appears on your passport, including any middle names
          <br />
          <RequiredFields />
        </>
      ),
    },
  ];

  steps.push({
    key: RepInfoSteps.Passport,
    name: "Passport",
    title: "Passport Details",
    description: (
      <>
        Please enter all of your information accurately, as it appears on the
        passport. <RequiredFields />
        <br />
        IMPORTANT NOTE: Please ensure you check the UK government’s foreign
        travel advice for any destinations (and transit countries){" "}
        <a href="https://www.gov.uk/foreign-travel-advice" target="_blank">
          https://www.gov.uk/foreign-travel-advice
        </a>
        . You are responsible for ensuring that all Entry Requirements
        requirements are met. Select cannot accept responsibility for any issues
        arising from individuals not adhering to entry (or vaccination)
        regulations, nor are these insurable.
      </>
    ),
  });

  steps.push({
    key: RepInfoSteps.Medical,
    name: "Medical and Dietary",
    title: "Medical and Dietary Information",
    description: (
      <>
        Please enter all of your information accurately
        <br />
        <RequiredFields />
      </>
    ),
  });

  steps.push({
    key: RepInfoSteps.Additional,
    name: "Additional Info",
    title: "Additional Information",
    description: (
      <>
        Please enter all of your information accurately
        <br />
        <RequiredFields />
      </>
    ),
  });

  steps.push({
    key: RepInfoSteps.Submit,
    name: "Save & Confirm",
    title: "Save & Confirm",
    description: null,
  });
  return steps;
};

const initialState = {
  formData: {},
  loading: false,
  completedStepIndex: null,
  currentStepKey: null,
  steps: buildSteps(),
  isSubmitted: false,
  isConfirmed: false,
  submitting: false,
  saving: false,
  isValid: false,
  saveCount: 0,
  dirty: false,
};

export default (state = initialState, action) => {
  switch (action.type) {
    case ActionTypes.GetRepInfo: {
      const isOk = requestIsSuccess(action);
      const response = requestResponse(action);
      const loading = requestIsBusy(action);

      const steps = isOk ? buildSteps() : state.steps;
      const completedIndex = response?.completedStepIndex;
      const nextStepKey =
        isOk && response?.completedStepIndex != null
          ? completedIndex >= 0 && completedIndex < steps.length - 1
            ? steps[completedIndex + 1]?.key
            : null
          : state.currentStepKey;

      return {
        ...state,
        loading,
        formData: isOk
          ? {
              ...response.data,
              [RepInfoSteps.Additional]: {
                ...response.data.additional,
                drivingLicence:
                  response.data.additional.drivingLicence?.toString(),
              },
              [RepInfoSteps.Submit]: { agree: response.isConfirmed },
            }
          : state.formData,
        steps: steps,

        completedStepIndex: isOk
          ? response.isSubmitted
            ? steps.length - 1
            : response.completedStepIndex
          : state.completedStepIndex,

        currentStepKey: isOk ? nextStepKey || steps[0].key : steps[0].key,
        isSubmitted: isOk ? response.isSubmitted : state.isSubmitted,
        isConfirmed: isOk ? response.isConfirmed : state.isConfirmed,
        saveCount: 0,
        dirty: false,
      };
    }

    case ActionTypes.MoveStep: {
      const targetIndex = state.steps.findIndex(
        (x) => x.key == action.targetStepKey
      );
      const currentCompleteIndex =
        state.completedStepIndex == null ? -1 : state.completedStepIndex;

      return {
        ...state,
        currentStepKey: action.targetStepKey,
        completedStepIndex:
          currentCompleteIndex < targetIndex
            ? targetIndex - 1
            : state.completedStepIndex,
      };
    }

    case ActionTypes.UpdateForm: {
      return {
        ...state,
        dirty: true,
        formData: action.applyChanges(state.formData),
      };
    }

    case ActionTypes.SaveStep: {
      const isConfirm = action.customData?.confirm == true;
      const isDirty = action.customData?.isDirty == true;
      const submitting = requestIsBusy(action) && isConfirm;
      const saving = requestIsBusy(action) && !isConfirm;

      const finishedSubmitting =
        (state.submitting && !submitting) || (state.saving && !saving);

      return {
        ...state,
        submitting,
        saving: isDirty ? saving : state.saving,
        isSubmitted: finishedSubmitting && isConfirm ? true : state.isSubmitted,
        saveCount: state.saveCount + (finishedSubmitting ? 1 : 0),
        dirty: submitting || saving ? false : state.dirty,
        completedStepIndex:
          finishedSubmitting && isConfirm
            ? state.steps.length - 1
            : state.completedStepIndex,
      };
    }

    case ActionTypes.SetValidation: {
      return {
        ...state,
        isValid: !!action.isValid,
      };
    }

    default:
      return state;
  }
};
